import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import LetterEditor from '../components/LetterEditor';
import LetterGenerator from '../components/LetterGenerator';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ArrowLeft, Edit, Save, FileText, Plus, Download, Printer, Eye, EyeOff, Loader2, FilePlus, Wand2, Trash2, Pencil, Check, X } from 'lucide-react';
import { format, isValid } from 'date-fns';
import { toast } from 'sonner';
import DocumentsSection from '../components/DocumentsSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const safeFormat = (dateStr, fmt = 'MMM d, yyyy') => {
  if (!dateStr) return '-';
  try {
    // Try adding T00:00:00 for date-only strings
    let d = new Date(dateStr);
    if (!isValid(d) && !dateStr.includes('T')) d = new Date(dateStr + 'T00:00:00');
    if (!isValid(d)) return dateStr; // Return raw string if can't parse
    return format(d, fmt);
  } catch { return dateStr; }
};

const statusColors = {
  Draft: 'bg-slate-100 text-slate-800',
  Final: 'bg-blue-100 text-blue-800',
  Printed: 'bg-emerald-100 text-emerald-800',
  Sent: 'bg-purple-100 text-purple-800'
};

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [scores, setScores] = useState([]);
  const [letters, setLetters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [notes, setNotes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSSN, setShowSSN] = useState(false);
  const [showCard, setShowCard] = useState(false);
  
  // Score form
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [scoreForm, setScoreForm] = useState({ experian: '', equifax: '', transunion: '', recorded_date: '', notes: '' });
  
  // Letter editor
  const [showLetterEditor, setShowLetterEditor] = useState(false);
  const [editingLetter, setEditingLetter] = useState(null);
  const [isNewLetter, setIsNewLetter] = useState(false);
  const [showLetterGenerator, setShowLetterGenerator] = useState(false);
  
  // Note form
  const [noteContent, setNoteContent] = useState('');
  const [noteVisible, setNoteVisible] = useState(false);

  // Inline editing
  const [editingScoreId, setEditingScoreId] = useState(null);
  const [editScoreForm, setEditScoreForm] = useState({});
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteForm, setEditNoteForm] = useState({});

  // Payment form
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_date: '', payment_method: '', status: 'completed', notes: '', reference_id: '' });
  const [paymentSaving, setPaymentSaving] = useState(false);

  // Edit client
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [partnersList, setPartnersList] = useState([]);

  const canEdit = ['aline', 'administrative', 'operator', 'office_admin'].includes(user?.role);
  const canSeeSensitive = ['aline', 'administrative', 'office_admin'].includes(user?.role);

  useEffect(() => {
    fetchAllData();
  }, [clientId]);

  const fetchAllData = async () => {
    try {
      const [clientRes, scoresRes, lettersRes, templatesRes, notesRes, paymentsRes, partnersRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients/${clientId}`),
        axios.get(`${API_URL}/api/credit-scores/${clientId}`),
        axios.get(`${API_URL}/api/client-letters/${clientId}`),
        axios.get(`${API_URL}/api/letter-templates`),
        axios.get(`${API_URL}/api/notes/${clientId}`),
        axios.get(`${API_URL}/api/payments/client/${clientId}`),
        canEdit ? axios.get(`${API_URL}/api/partners`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);
      setClient(clientRes.data);
      setScores(scoresRes.data);
      setLetters(lettersRes.data);
      setTemplates(templatesRes.data);
      setNotes(notesRes.data);
      setPayments(paymentsRes.data);
      setPartnersList(partnersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddScore = async () => {
    try {
      await axios.post(`${API_URL}/api/credit-scores`, {
        client_id: clientId,
        experian: parseInt(scoreForm.experian) || null,
        equifax: parseInt(scoreForm.equifax) || null,
        transunion: parseInt(scoreForm.transunion) || null,
        recorded_date: scoreForm.recorded_date || null,
        notes: scoreForm.notes || null
      });
      setShowScoreDialog(false);
      setScoreForm({ experian: '', equifax: '', transunion: '', recorded_date: '', notes: '' });
      fetchAllData();
    } catch (error) {
      console.error('Error adding score:', error);
    }
  };

  const handleAddPayment = async () => {
    try {
      setPaymentSaving(true);
      const totalInstallments = client?.number_of_installments || client?.installments || null;
      const completedPayments = payments.filter(p => p.status === 'completed').length;
      const nextInstallment = totalInstallments ? completedPayments + 1 : null;
      
      await axios.post(`${API_URL}/api/payments`, {
        client_id: clientId,
        amount: parseFloat(paymentForm.amount) || 0,
        payment_date: paymentForm.payment_date || new Date().toISOString().split('T')[0],
        payment_method: paymentForm.payment_method || '',
        status: paymentForm.status || 'completed',
        notes: paymentForm.notes || '',
        reference_id: paymentForm.reference_id || '',
        installment_number: nextInstallment,
        total_installments: totalInstallments
      });
      setShowPaymentDialog(false);
      setPaymentForm({ amount: '', payment_date: '', payment_method: '', status: 'completed', notes: '', reference_id: '' });
      fetchAllData();
    } catch (error) {
      console.error('Error adding payment:', error);
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleCreateNewLetter = () => {
    setEditingLetter(null);
    setIsNewLetter(true);
    setShowLetterEditor(true);
  };

  const handleGenerateLetter = () => {
    setShowLetterGenerator(true);
  };

  const handleLetterGenerated = (generatedLetter) => {
    // Open the generated letter in the editor for review/editing
    setEditingLetter(generatedLetter);
    setIsNewLetter(false);
    setShowLetterEditor(true);
    fetchAllData();
  };

  const handleEditLetter = (letter) => {
    setEditingLetter(letter);
    setIsNewLetter(false);
    setShowLetterEditor(true);
  };

  const handleSaveLetter = async (letterData) => {
    try {
      if (isNewLetter) {
        // Create new letter
        await axios.post(`${API_URL}/api/client-letters`, {
          client_id: clientId,
          template_id: letterData.template_id || '',
          title: letterData.title,
          content: letterData.content,
          letter_type: letterData.letter_type,
          status: letterData.status,
          bureau: letterData.bureau
        });
      } else {
        // Update existing letter
        await axios.put(`${API_URL}/api/client-letters/${letterData.id}`, {
          title: letterData.title,
          content: letterData.content,
          letter_type: letterData.letter_type,
          status: letterData.status,
          bureau: letterData.bureau
        });
      }
      setShowLetterEditor(false);
      setEditingLetter(null);
      fetchAllData();
    } catch (error) {
      console.error('Error saving letter:', error);
    }
  };

  const handlePrintLetter = async (letter) => {
    try {
      const response = await axios.post(`${API_URL}/api/letters/generate-pdf`, {
        content: letter.content,
        title: letter.title || 'Letter'
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(letter.title || 'Letter').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate PDF');
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) return;
    try {
      await axios.post(`${API_URL}/api/notes`, {
        client_id: clientId,
        content: noteContent,
        visible_to_client: noteVisible
      });
      setNoteContent('');
      setNoteVisible(false);
      fetchAllData();
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleDeleteScore = async (scoreId) => {
    if (!window.confirm('Remove this score entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/credit-scores/${scoreId}`);
      fetchAllData();
    } catch (error) {
      console.error('Error deleting score:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Remove this note?')) return;
    try {
      await axios.delete(`${API_URL}/api/notes/${noteId}`);
      fetchAllData();
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const startEditScore = (score) => {
    setEditingScoreId(score.id);
    setEditScoreForm({
      experian: score.experian || '',
      equifax: score.equifax || '',
      transunion: score.transunion || '',
      recorded_date: score.recorded_date ? score.recorded_date.split('T')[0] : '',
      notes: score.notes || ''
    });
  };

  const handleSaveScore = async () => {
    try {
      await axios.put(`${API_URL}/api/credit-scores/${editingScoreId}`, {
        experian: parseInt(editScoreForm.experian) || null,
        equifax: parseInt(editScoreForm.equifax) || null,
        transunion: parseInt(editScoreForm.transunion) || null,
        recorded_date: editScoreForm.recorded_date || null,
        notes: editScoreForm.notes || null
      });
      setEditingScoreId(null);
      fetchAllData();
    } catch (error) {
      console.error('Error updating score:', error);
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditNoteForm({
      content: note.content || '',
      visible_to_client: note.visible_to_client || false
    });
  };

  const handleSaveNote = async () => {
    try {
      await axios.put(`${API_URL}/api/notes/${editingNoteId}`, editNoteForm);
      setEditingNoteId(null);
      fetchAllData();
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const openEditDialog = () => {
    setEditForm({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      phone: client.phone || '',
      address: client.address || '',
      dob: client.dob || '',
      referral_name: client.referral_name || '',
      partner_id: client.partner_id || '',
      payment_plan: client.payment_plan || 'Month',
      monthly_payment_amount: client.monthly_payment_amount || 0,
      number_of_installments: client.number_of_installments || client.installments || 1,
      contract_value: client.contract_value || 0,
      payment_status: client.payment_status || 'Not Started',
      status: client.status || 'Active Client'
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    try {
      await axios.put(`${API_URL}/api/clients/${clientId}`, editForm);
      setShowEditDialog(false);
      fetchAllData();
    } catch (error) {
      console.error('Error updating client:', error);
    } finally {
      setEditSaving(false);
    }
  };

  const chartData = scores.map((s) => ({
    month: s.recorded_date ? safeFormat(s.recorded_date, 'MMM d') : 
           s.updated_at ? safeFormat(s.updated_at, 'MMM d') : 'N/A',
    Experian: s.experian,
    Equifax: s.equifax,
    TransUnion: s.transunion
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!client) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Client not found</p>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
            Go Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          {canEdit && (
            <Button
              className="bg-[#1a2e5a] hover:bg-[#15254a] text-white"
              onClick={openEditDialog}
              data-testid="edit-client-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Client
            </Button>
          )}
        </div>

        {/* Edit Client Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Client — {client.first_name} {client.last_name}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div>
                <Label htmlFor="edit_first_name">First Name</Label>
                <Input id="edit_first_name" value={editForm.first_name || ''} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} data-testid="edit-first-name" />
              </div>
              <div>
                <Label htmlFor="edit_last_name">Last Name</Label>
                <Input id="edit_last_name" value={editForm.last_name || ''} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} data-testid="edit-last-name" />
              </div>
              <div>
                <Label htmlFor="edit_phone">Phone</Label>
                <Input id="edit_phone" value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-phone" />
              </div>
              <div>
                <Label htmlFor="edit_dob">Date of Birth</Label>
                <Input id="edit_dob" type="date" value={editForm.dob || ''} onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })} data-testid="edit-dob" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="edit_address">Address</Label>
                <Input id="edit_address" value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} data-testid="edit-address" />
              </div>
              <div>
                <Label htmlFor="edit_referral">Referral Name</Label>
                <Input id="edit_referral" value={editForm.referral_name || ''} onChange={(e) => setEditForm({ ...editForm, referral_name: e.target.value })} data-testid="edit-referral" />
              </div>
              <div>
                <Label htmlFor="edit_partner">Linked Partner</Label>
                <Select value={editForm.partner_id || 'none'} onValueChange={(v) => setEditForm({ ...editForm, partner_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="edit-partner-id">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Partner</SelectItem>
                    {partnersList.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name} ({p.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_status">Status</Label>
                <Select value={editForm.status || 'Active Client'} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger data-testid="edit-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active Client">Active Client</SelectItem>
                    <SelectItem value="Collection Attention">Collection Attention</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_payment_plan">Payment Plan</Label>
                <Select value={editForm.payment_plan || 'Month'} onValueChange={(v) => setEditForm({ ...editForm, payment_plan: v })}>
                  <SelectTrigger data-testid="edit-payment-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Month">Monthly</SelectItem>
                    <SelectItem value="Bi-Weekly">Bi-Weekly</SelectItem>
                    <SelectItem value="Weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_monthly">Monthly Payment ($)</Label>
                <Input id="edit_monthly" type="number" value={editForm.monthly_payment_amount || ''} onChange={(e) => setEditForm({ ...editForm, monthly_payment_amount: parseFloat(e.target.value) || 0 })} data-testid="edit-monthly-payment" />
              </div>
              <div>
                <Label htmlFor="edit_installments">Installments</Label>
                <Input id="edit_installments" type="number" value={editForm.number_of_installments || ''} onChange={(e) => setEditForm({ ...editForm, number_of_installments: parseInt(e.target.value) || 1 })} data-testid="edit-installments" />
              </div>
              <div>
                <Label htmlFor="edit_contract">Contract Value ($)</Label>
                <Input id="edit_contract" type="number" value={editForm.contract_value || ''} onChange={(e) => setEditForm({ ...editForm, contract_value: parseFloat(e.target.value) || 0 })} data-testid="edit-contract-value" />
              </div>
              <div>
                <Label htmlFor="edit_payment_status">Payment Status</Label>
                <Select value={editForm.payment_status || 'Not Started'} onValueChange={(v) => setEditForm({ ...editForm, payment_status: v })}>
                  <SelectTrigger data-testid="edit-payment-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Overdue">Overdue</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button className="bg-[#1a2e5a] hover:bg-[#15254a] text-white" onClick={handleSaveEdit} disabled={editSaving} data-testid="save-edit-btn">
                {editSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Client Info Card */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="client-info-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-2xl font-semibold text-blue-600">
                    {client.first_name?.[0]}{client.last_name?.[0]}
                  </span>
                </div>
                <div>
                  <CardTitle className="text-2xl">{client.first_name} {client.last_name}</CardTitle>
                  <div className="flex items-center gap-3 mt-1">
                    {client.client_code && (
                      <span className="font-mono text-sm text-blue-600 bg-blue-50 px-2 py-0.5 rounded" data-testid="client-code">{client.client_code}</span>
                    )}
                    <span className="text-slate-500">{client.email}</span>
                  </div>
                </div>
              </div>
              <Badge className={
                client.status === 'Active Client' || client.status === 'active'
                  ? 'bg-emerald-100 text-emerald-800'
                  : client.status === 'Collection Attention'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-slate-100 text-slate-800'
              }>
                {client.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm font-medium text-slate-500">Phone</p>
                <p className="text-slate-900">{client.phone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Address</p>
                <p className="text-slate-900">{client.address || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Date of Birth</p>
                <p className="text-slate-900">{client.dob ? safeFormat(client.dob) : 'Not provided'}</p>
              </div>
              {canSeeSensitive ? (
                <>
                  <div>
                    <p className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      SSN
                      <button onClick={() => setShowSSN(!showSSN)} className="text-blue-600">
                        {showSSN ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </p>
                    <p className="text-slate-900 font-mono">
                      {showSSN ? client.ssn : client.ssn_masked || '***-**-****'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      Card Number
                      <button onClick={() => setShowCard(!showCard)} className="text-blue-600">
                        {showCard ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </p>
                    <p className="text-slate-900 font-mono">
                      {showCard ? client.card_number : client.card_masked || '**** **** **** ****'}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-sm font-medium text-slate-500">SSN</p>
                    <p className="text-slate-900 font-mono">{client.ssn_masked || '***-**-****'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Card Number</p>
                    <p className="text-slate-900 font-mono">{client.card_masked || '**** **** **** ****'}</p>
                  </div>
                </>
              )}
              <div>
                <p className="text-sm font-medium text-slate-500">Referral</p>
                <p className="text-slate-900">{client.referral_name || 'None'}</p>
                {client.partner_id && partnersList.length > 0 && (
                  <p className="text-xs text-blue-600 mt-0.5">
                    Linked to: {partnersList.find(p => p.id === client.partner_id)?.name || 'Partner'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Monthly Payment</p>
                <p className="text-slate-900">{client.monthly_payment_amount ? `$${client.monthly_payment_amount.toLocaleString()}` : 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Installments</p>
                <p className="text-slate-900">{client.number_of_installments || client.installments || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Contract Value</p>
                <p className="text-slate-900">${client.contract_value?.toLocaleString() || 0}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Payment Plan</p>
                <p className="text-slate-900">{client.payment_plan || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Payment Status</p>
                <Badge className={
                  client.payment_status === 'Not Started' ? 'bg-slate-100 text-slate-800' :
                  client.payment_status === 'Completed' ? 'bg-emerald-100 text-emerald-800' :
                  'bg-amber-100 text-amber-800'
                } data-testid="payment-status-badge">
                  {client.payment_status || 'Not Started'}
                </Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Created Date</p>
                <p className="text-slate-900">
                  {client.created_at ? safeFormat(client.created_at) : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="scores" className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="scores" data-testid="tab-scores">Credit Scores</TabsTrigger>
            <TabsTrigger value="letters" data-testid="tab-letters">Letters ({letters.length})</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          </TabsList>

          {/* Credit Scores Tab */}
          <TabsContent value="scores" className="space-y-4">
            <Card className="bg-white border-slate-200 shadow-sm" data-testid="scores-chart-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Credit Score History</CardTitle>
                {canEdit && (
                  <Dialog open={showScoreDialog} onOpenChange={setShowScoreDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-score-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Score
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Credit Scores</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="recorded_date">Recorded Date</Label>
                          <Input
                            id="recorded_date"
                            type="date"
                            value={scoreForm.recorded_date || ''}
                            onChange={(e) => setScoreForm({ ...scoreForm, recorded_date: e.target.value })}
                            data-testid="input-recorded-date"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label htmlFor="experian">Experian</Label>
                            <Input
                              id="experian"
                              type="number"
                              min="300"
                              max="850"
                              value={scoreForm.experian}
                              onChange={(e) => setScoreForm({ ...scoreForm, experian: e.target.value })}
                              data-testid="input-experian"
                            />
                          </div>
                          <div>
                            <Label htmlFor="equifax">Equifax</Label>
                            <Input
                              id="equifax"
                              type="number"
                              min="300"
                              max="850"
                              value={scoreForm.equifax}
                              onChange={(e) => setScoreForm({ ...scoreForm, equifax: e.target.value })}
                              data-testid="input-equifax"
                            />
                          </div>
                          <div>
                            <Label htmlFor="transunion">TransUnion</Label>
                            <Input
                              id="transunion"
                              type="number"
                              min="300"
                              max="850"
                              value={scoreForm.transunion}
                              onChange={(e) => setScoreForm({ ...scoreForm, transunion: e.target.value })}
                              data-testid="input-transunion"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="notes">Notes (Optional)</Label>
                          <Input
                            id="notes"
                            value={scoreForm.notes || ''}
                            onChange={(e) => setScoreForm({ ...scoreForm, notes: e.target.value })}
                            placeholder="e.g., After dispute resolution"
                            data-testid="input-score-notes"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button onClick={handleAddScore} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-score">
                          Save Scores
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                        <YAxis domain={[300, 850]} stroke="#64748b" fontSize={12} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                        <Legend />
                        <Line type="monotone" dataKey="Experian" stroke="#2563EB" strokeWidth={2} dot={{ fill: '#2563EB' }} />
                        <Line type="monotone" dataKey="Equifax" stroke="#059669" strokeWidth={2} dot={{ fill: '#059669' }} />
                        <Line type="monotone" dataKey="TransUnion" stroke="#D97706" strokeWidth={2} dot={{ fill: '#D97706' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-500">
                      No score data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Latest Scores */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-white border-slate-200 shadow-sm" data-testid="latest-experian">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-12 rounded-full bg-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-500">Experian</p>
                      <p className="text-4xl font-semibold text-slate-900">{scores[scores.length - 1]?.experian || '---'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200 shadow-sm" data-testid="latest-equifax">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-12 rounded-full bg-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-500">Equifax</p>
                      <p className="text-4xl font-semibold text-slate-900">{scores[scores.length - 1]?.equifax || '---'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200 shadow-sm" data-testid="latest-transunion">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-12 rounded-full bg-amber-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-500">TransUnion</p>
                      <p className="text-4xl font-semibold text-slate-900">{scores[scores.length - 1]?.transunion || '---'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Score History Table */}
            {scores.length > 0 && (
              <Card className="bg-white border-slate-200 shadow-sm" data-testid="score-history-table">
                <CardHeader>
                  <CardTitle className="text-lg">Score History</CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Date</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase text-center">Experian</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase text-center">Equifax</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase text-center">TransUnion</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Recorded By</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Notes</TableHead>
                        {canEdit && <TableHead className="text-xs font-medium text-slate-500 uppercase w-20"></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...scores].reverse().map((score, index) => (
                        editingScoreId === score.id ? (
                          <TableRow key={score.id} className="bg-blue-50/50">
                            <TableCell>
                              <Input
                                type="date"
                                value={editScoreForm.recorded_date}
                                onChange={(e) => setEditScoreForm(f => ({...f, recorded_date: e.target.value}))}
                                className="h-8 text-sm w-32"
                                data-testid="edit-score-date"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={editScoreForm.experian}
                                onChange={(e) => setEditScoreForm(f => ({...f, experian: e.target.value}))}
                                className="h-8 text-sm w-20 text-center"
                                data-testid="edit-score-experian"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={editScoreForm.equifax}
                                onChange={(e) => setEditScoreForm(f => ({...f, equifax: e.target.value}))}
                                className="h-8 text-sm w-20 text-center"
                                data-testid="edit-score-equifax"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={editScoreForm.transunion}
                                onChange={(e) => setEditScoreForm(f => ({...f, transunion: e.target.value}))}
                                className="h-8 text-sm w-20 text-center"
                                data-testid="edit-score-transunion"
                              />
                            </TableCell>
                            <TableCell className="text-slate-600 text-sm">{score.recorded_by || 'System'}</TableCell>
                            <TableCell>
                              <Input
                                value={editScoreForm.notes}
                                onChange={(e) => setEditScoreForm(f => ({...f, notes: e.target.value}))}
                                className="h-8 text-sm"
                                placeholder="Notes..."
                                data-testid="edit-score-notes"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={handleSaveScore}
                                  className="p-1.5 rounded-md hover:bg-emerald-50 text-emerald-600 transition-colors"
                                  title="Save"
                                  data-testid="save-score-btn"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => setEditingScoreId(null)}
                                  className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 transition-colors"
                                  title="Cancel"
                                  data-testid="cancel-edit-score-btn"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                        <TableRow key={score.id || index} className="hover:bg-slate-50">
                          <TableCell className="text-slate-900">
                            {score.recorded_date ? safeFormat(score.recorded_date) : 
                             score.updated_at ? safeFormat(score.updated_at) : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-semibold text-blue-600">{score.experian || '-'}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-semibold text-emerald-600">{score.equifax || '-'}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-semibold text-amber-600">{score.transunion || '-'}</span>
                          </TableCell>
                          <TableCell className="text-slate-600">{score.recorded_by || 'System'}</TableCell>
                          <TableCell className="text-slate-500 text-sm">{score.notes || '-'}</TableCell>
                          {canEdit && (
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => startEditScore(score)}
                                  className="p-1.5 rounded-md hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
                                  title="Edit this score"
                                  data-testid={`edit-score-${score.id}`}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteScore(score.id)}
                                  className="p-1.5 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-colors"
                                  title="Remove this score"
                                  data-testid={`delete-score-${score.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                        )
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Letters Tab - ENHANCED */}
          <TabsContent value="letters" className="space-y-4">
            <Card className="bg-white border-slate-200 shadow-sm" data-testid="letters-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Client Letters</CardTitle>
                  <p className="text-sm text-slate-500 mt-1">All letters created for {client.first_name}</p>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline"
                      onClick={handleCreateNewLetter}
                      data-testid="create-new-letter-btn"
                    >
                      <FilePlus className="w-4 h-4 mr-2" />
                      From Template
                    </Button>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700" 
                      onClick={handleGenerateLetter}
                      data-testid="generate-letter-btn"
                    >
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate Letter
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {letters.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500 mb-4">No letters created yet</p>
                    {canEdit && (
                      <div className="flex items-center justify-center gap-3">
                        <Button variant="outline" onClick={handleCreateNewLetter}>
                          <FilePlus className="w-4 h-4 mr-2" />
                          From Template
                        </Button>
                        <Button onClick={handleGenerateLetter} className="bg-blue-600 hover:bg-blue-700">
                          <Wand2 className="w-4 h-4 mr-2" />
                          Generate Letter
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Title</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Type</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Bureau</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Status</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Date</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {letters.map((letter) => (
                        <TableRow key={letter.id} className="hover:bg-slate-50" data-testid={`letter-row-${letter.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-blue-600" />
                              <div>
                                <span className="font-medium text-slate-900">{letter.title}</span>
                                {letter.is_generated && (
                                  <span className="ml-2 text-xs text-blue-600">(Auto)</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-600">{letter.letter_type || '-'}</TableCell>
                          <TableCell>
                            {letter.bureau ? (
                              <Badge variant="outline" className="text-xs">{letter.bureau}</Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[letter.status] || 'bg-slate-100 text-slate-800'}>
                              {letter.status || 'Draft'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-600">
                            {letter.created_at ? safeFormat(letter.created_at) : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleEditLetter(letter)}
                                data-testid={`edit-letter-${letter.id}`}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Open/Edit
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handlePrintLetter(letter)}
                                data-testid={`print-letter-${letter.id}`}
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card className="bg-white border-slate-200 shadow-sm" data-testid="payments-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Payment History</CardTitle>
                {canEdit && (
                  <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-payment-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add Payment
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Record Payment</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-2">
                        {client && (
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                            <p className="font-medium text-slate-900">{client.first_name} {client.last_name}</p>
                            <p className="text-slate-500">Monthly: ${client.monthly_payment_amount || 0} | Installments: {client.number_of_installments || client.installments || '-'}</p>
                            <p className="text-slate-500">Next: Installment {Math.min(payments.filter(p => p.status === 'completed').length + 1, client.number_of_installments || client.installments || 999)} of {client.number_of_installments || client.installments || '-'}</p>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="pay_amount">Amount ($)</Label>
                            <Input id="pay_amount" type="number" placeholder={client?.monthly_payment_amount?.toString() || '0'} value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})} data-testid="payment-amount-input" />
                          </div>
                          <div>
                            <Label htmlFor="pay_date">Date</Label>
                            <Input id="pay_date" type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})} data-testid="payment-date-input" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="pay_method">Method</Label>
                            <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                              <SelectTrigger data-testid="payment-method-select"><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Zelle">Zelle</SelectItem>
                                <SelectItem value="Debit Card">Debit Card</SelectItem>
                                <SelectItem value="Credit Card">Credit Card</SelectItem>
                                <SelectItem value="Cash">Cash</SelectItem>
                                <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="pay_status">Status</Label>
                            <Select value={paymentForm.status} onValueChange={(v) => setPaymentForm({...paymentForm, status: v})}>
                              <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="overdue">Overdue</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="pay_ref">Reference ID</Label>
                          <Input id="pay_ref" value={paymentForm.reference_id} onChange={(e) => setPaymentForm({...paymentForm, reference_id: e.target.value})} placeholder="Optional" data-testid="payment-reference-input" />
                        </div>
                        <div>
                          <Label htmlFor="pay_notes">Notes</Label>
                          <Input id="pay_notes" value={paymentForm.notes} onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})} placeholder="Optional" data-testid="payment-notes-input" />
                        </div>
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button onClick={handleAddPayment} disabled={paymentSaving || !paymentForm.amount} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-payment-btn">
                          {paymentSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                          Save Payment
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {payments.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No payments recorded yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Date</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase text-right">Amount</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Installment</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Method</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Status</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Reference</TableHead>
                        <TableHead className="text-xs font-medium text-slate-500 uppercase">Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.id || payment.payment_date} className="hover:bg-slate-50">
                          <TableCell className="text-slate-900">{payment.payment_date || '-'}</TableCell>
                          <TableCell className="text-right font-semibold text-slate-900">${payment.amount?.toLocaleString()}</TableCell>
                          <TableCell className="text-slate-600 text-sm">
                            {payment.installment_number && payment.total_installments
                              ? <div>
                                  <span className="font-medium">{payment.installment_number} of {payment.total_installments}</span>
                                  <span className="block text-xs text-slate-400">Remaining: {Math.max(payment.total_installments - payment.installment_number, 0)}</span>
                                </div>
                              : '-'
                            }
                          </TableCell>
                          <TableCell className="text-slate-600">{payment.payment_method || '-'}</TableCell>
                          <TableCell>
                            <Badge className={
                              payment.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                              payment.status === 'overdue' ? 'bg-rose-100 text-rose-800' :
                              'bg-amber-100 text-amber-800'
                            }>
                              {payment.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">{payment.reference_id || '-'}</TableCell>
                          <TableCell className="text-slate-500 text-sm">{payment.notes || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes">
            <Card className="bg-white border-slate-200 shadow-sm" data-testid="notes-card">
              <CardHeader>
                <CardTitle>Notes & Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {canEdit && (
                  <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                    <Textarea
                      placeholder="Add a note..."
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      rows={3}
                      data-testid="input-note"
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-slate-600">
                        <input
                          type="checkbox"
                          checked={noteVisible}
                          onChange={(e) => setNoteVisible(e.target.checked)}
                          className="rounded border-slate-300"
                        />
                        Visible to client
                      </label>
                      <Button size="sm" onClick={handleAddNote} className="bg-blue-600 hover:bg-blue-700" data-testid="add-note-btn">
                        Add Note
                      </Button>
                    </div>
                  </div>
                )}
                
                {notes.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No notes yet</p>
                ) : (
                  <div className="space-y-3">
                    {notes.map((note, index) => (
                      editingNoteId === note.id ? (
                        <div key={note.id} className="p-4 border border-blue-200 rounded-lg bg-blue-50/30">
                          <Textarea
                            value={editNoteForm.content}
                            onChange={(e) => setEditNoteForm(f => ({...f, content: e.target.value}))}
                            rows={3}
                            className="mb-3"
                            data-testid="edit-note-content"
                          />
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-sm text-slate-600">
                              <input
                                type="checkbox"
                                checked={editNoteForm.visible_to_client}
                                onChange={(e) => setEditNoteForm(f => ({...f, visible_to_client: e.target.checked}))}
                                className="rounded border-slate-300"
                                data-testid="edit-note-visible"
                              />
                              Visible to client
                            </label>
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="ghost" onClick={() => setEditingNoteId(null)} data-testid="cancel-edit-note-btn">
                                Cancel
                              </Button>
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleSaveNote} data-testid="save-note-btn">
                                <Check className="w-3.5 h-3.5 mr-1" /> Save
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                      <div
                        key={note.id || index}
                        className="p-4 border border-slate-200 rounded-lg"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-slate-900 flex-1">{note.content}</p>
                          {canEdit && note.id && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => startEditNote(note)}
                                className="p-1.5 rounded-md hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
                                title="Edit this note"
                                data-testid={`edit-note-${note.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteNote(note.id)}
                                className="p-1.5 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-colors"
                                title="Remove this note"
                                data-testid={`delete-note-${note.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                          <span>By {note.created_by_name}</span>
                          <span>•</span>
                          <span>{safeFormat(note.created_at)}</span>
                          {note.visible_to_client && (
                            <Badge variant="outline" className="text-xs">Client visible</Badge>
                          )}
                        </div>
                      </div>
                      )
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <DocumentsSection
              clientId={clientId}
              canDelete={canSeeSensitive}
              userRole={user?.role}
            />
          </TabsContent>
        </Tabs>
        {showLetterGenerator && (
          <LetterGenerator
            isOpen={showLetterGenerator}
            onClose={() => setShowLetterGenerator(false)}
            clientId={clientId}
            onGenerated={handleLetterGenerated}
          />
        )}

        {/* Letter Editor Modal */}
        {showLetterEditor && (
          <LetterEditor
            isOpen={showLetterEditor}
            onClose={() => {
              setShowLetterEditor(false);
              setEditingLetter(null);
            }}
            letter={editingLetter}
            templates={templates}
            client={client}
            onSave={handleSaveLetter}
            isNew={isNewLetter}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
